// "When was this queue last emptied" — the strip at the top of the Submit queue page.
//
// The queue's own summary answers "what is in it". This answers the question a biller
// actually asks on a Monday: has anybody drained it, who, and how long has it been.
// `lastEmptiedAt` is derived on the server (the finish time of the last run after which
// the queue was empty), so it is read, never computed here.
//
// PHI: counts, an operator's email and two timestamps. No claim, no patient.

import React from "react";
import { portalLabel } from "./api";
import { relativeTime } from "./ageing";
import "./Queue.css";

// `text` marks a value that is words rather than a number ("2 days ago", an email):
// the big numeral face is for counts and turns a sentence into a wrapped mess.
function Stat({ label, value, tone = "", title, text = false }) {
  return (
    <div className={`sq-stat ${tone} ${text ? "sq-stat-text" : ""}`} title={title}>
      <span className="sq-stat-value">{value}</span>
      <span className="sq-stat-label">{label}</span>
    </div>
  );
}

// `lastEmptiedAt` is anchored on a finished skill run, so it is BLANK — not wrong,
// blank — for a queue a person drained by hand with "I submitted" and no run. Saying
// "never emptied" over an empty queue would be a plain falsehood, and the biller who
// just cleared it is the one reading the line. Depth tells the two apart.
export function emptiedLabel(drain) {
  if (drain.lastEmptiedAt) {
    return `Last emptied ${relativeTime(drain.lastEmptiedAt)}${
      drain.lastEmptiedRunId ? ` (run #${drain.lastEmptiedRunId})` : ""
    }`;
  }
  if (drain.queueDepth === 0) {
    return "Empty now — no skill run has recorded a drain, so there is no date";
  }
  return "Never emptied — no finished run has left this queue at zero";
}

export default function DrainHeader({ drain, error }) {
  if (error) {
    return (
      <div className="sq-drain-note" role="status">
        <span>{error}</span>
      </div>
    );
  }
  if (!drain) return null;

  const portal = portalLabel(drain.portal);
  const run = drain.lastRun;

  return (
    <div className="sq-drain" aria-label="Drain status">
      <div className="sq-drain-line">
        <span className="sq-drain-title">
          {portal ? (
            <>
              Drains to <strong>{portal}</strong>
            </>
          ) : (
            "No submit portal set for this practice"
          )}
        </span>
        <span className="sq-muted">{emptiedLabel(drain)}</span>
      </div>

      <div className="sq-summary sq-drain-stats">
        <Stat
          label="In the queue"
          value={drain.queueDepth}
          tone={drain.queueDepth > 0 ? "" : "grey"}
          title="Queued, being worked and held"
        />
        <Stat
          label="Needs a person"
          value={drain.held}
          tone={drain.held > 0 ? "red" : ""}
          title="Held rows: submit was pressed and no reference was captured"
        />
        {run ? (
          <>
            <Stat
              label="Last run"
              value={run.finishedAt ? relativeTime(run.finishedAt) : "—"}
              text
              title={`Run #${run.id}, ${run.status}`}
            />
            <Stat
              label="Submitted then"
              value={run.submittedCount}
              title={
                run.failedCount
                  ? `${run.failedCount} failed in the same run`
                  : undefined
              }
            />
            <Stat
              label="Operator"
              value={run.operator || "—"}
              text
              title={run.operator || "The run recorded no operator"}
            />
          </>
        ) : (
          <Stat label="Last run" value="never" tone="grey" text />
        )}
      </div>

      {run && run.needsHuman && (
        <div className="sq-drain-note amber" role="status">
          <span>
            The last run ended needing a person. The rows it could not finish are in
            the list below.
          </span>
        </div>
      )}
    </div>
  );
}
